export enum UserRole {
  AGENT = "agent",
  CLIENT = "client",
  LENDER = "lender",
  ATTORNEY = "attorney",
  VENDOR = "vendor",
}

export enum UserKind {
  INTERNAL = "internal",
  EXTERNAL = "external",
}

export enum OrganizationStatus {
  TRIALING = "trialing",
  ACTIVE = "active",
  PAST_DUE = "past_due",
  CANCELED = "canceled",
  GRACE = "grace",
}

export enum OrganizationType {
  BROKERAGE = "brokerage",
  TEAM = "team",
}

export enum RouteAccess {
  INTERNAL = "internal",
  EXTERNAL = "external",
  ANY = "any",
}

export enum PlanInterest {
  BROKERAGE = "brokerage",
  ENTERPRISE = "enterprise",
  OTHER = "other",
}

// Invitation enums
export enum InternalRole {
  ORG_OWNER = "org_owner",
  BROKERAGE_ADMIN = "brokerage_admin",
  TEAM_LEAD = "team_lead",
  AGENT = "agent",
  COORDINATOR = "coordinator",
}

export enum InvitableInternalRole {
  BROKERAGE_ADMIN = "brokerage_admin",
  TEAM_LEAD = "team_lead",
  AGENT = "agent",
  COORDINATOR = "coordinator",
}

export enum InvitationStatus {
  PENDING = "pending",
  ACCEPTED = "accepted",
  EXPIRED = "expired",
  REVOKED = "revoked",
}

export enum InvitationKind {
  ORGANIZATION = "org",
  DEAL = "deal",
}

export enum EmailDeliveryStatus {
  SENT = "sent",
  FAILED = "failed",
  SKIPPED = "skipped",
}

export enum ParticipantRole {
  CLIENT = "client",
  LENDER = "lender",
  ATTORNEY = "attorney",
  VENDOR = "vendor",
  CO_AGENT = "co_agent",
}

export enum DealRole {
  AGENT = "agent",
  COORDINATOR = "coordinator",
  CLIENT = "client",
  LENDER = "lender",
  ATTORNEY = "attorney",
  VENDOR = "vendor",
  CO_AGENT = "co_agent",
}

export enum Side {
  SELL = "sell",
  BUY = "buy",
  NEUTRAL = "neutral",
}

// Deal enums
export enum DealType {
  SELL = "sell",
  BUY = "buy",
}

export enum Financing {
  FINANCED = "financed",
  CASH = "cash",
}

export enum FinancingContingencyMode {
  DAYS = "days",
  DATE = "date",
}

export enum DealPhase {
  PRE_LISTING = "pre_listing",
  LISTING = "listing",
  PRE_APPROVAL = "pre_approval",
  HOUSE_SHOPPING = "house_shopping",
  UNDER_CONTRACT = "under_contract",
  CLOSED = "closed",
}

export enum DealView {
  INTERNAL_FULL = "internal_full",
  CLIENT_CALM = "client_calm",
  EXTERNAL_FOCUSED = "external_focused",
}

export enum DealHealth {
  ON_TRACK = "on_track",
  AHEAD = "ahead",
  WATCHING = "watching",
  AT_RISK = "at_risk",
  /** Derived only — the escalation of AT_RISK once a deadline is already past.
   *  The server computes it; agents can only pin the three above. */
  BLOCKED = "blocked",
}

export enum BoardColumn {
  OPPORTUNITIES = "opportunities",
  ACTIVE = "active",
  UNDER_CONTRACT = "under_contract",
  CLOSED = "closed",
}

export enum PurchaseMethod {
  CASH = "cash",
  FINANCED = "financed",
}

export enum LoanType {
  CONVENTIONAL = "conventional",
  FHA = "fha",
  VA = "va",
  JUMBO = "jumbo",
  USDA = "usda",
  OTHER = "other",
}

export enum NextStepStatus {
  NOT_STARTED = "not_started",
  IN_PROGRESS = "in_progress",
  DONE = "done",
}

export enum SnapshotSource {
  AGENT = "agent",
  AUTO = "auto",
}

// Task enums
export enum TaskStatus {
  PENDING = "pending",
  IN_PROGRESS = "in_progress",
  COMPLETE = "complete",
}

export enum TaskPriority {
  HIGH = "high",
  MEDIUM = "medium",
  LOW = "low",
}

export enum DealAssigneeRole {
  AGENT = "agent",
  CLIENT = "client",
  LENDER = "lender",
  ATTORNEY = "attorney",
}

export enum SnapshotOwner {
  UNASSIGNED = "unassigned",
  AGENT = "agent",
  COORDINATOR = "coordinator",
  CLIENT = "client",
  LENDER = "lender",
  ATTORNEY = "attorney",
  CO_AGENT = "co_agent",
}

export enum DocumentCategory {
  CONTRACT = "Contract",
  DISCLOSURE = "Disclosure",
  INSPECTION = "Inspection",
  APPRAISAL = "Appraisal",
  TITLE = "Title",
  LOAN = "Loan",
  OTHER = "Other",
}

export enum CheckoutSessionStatus {
  OPEN = "open",
  COMPLETE = "complete",
  EXPIRED = "expired",
}

export enum PaymentStatus {
  PAID = "paid",
  UNPAID = "unpaid",
  NO_PAYMENT_REQUIRED = "no_payment_required",
}

export enum Visibility {
  INTERNAL = "internal",
  SHARED_WITH_EXTERNALS = "shared_with_externals",
  CLIENT_VISIBLE = "client_visible",
}

/** Axis 1 — who may ever see a row. */
export enum Audience {
  INTERNAL = "internal",
  EXTERNALS = "externals",
  CLIENT = "client",
}

/** Axis 2 — when an audience=client task reaches the client. */
export enum ClientRelease {
  AUTO = "auto",
  AFTER_CONFIRMED = "after_confirmed",
  ON_APPROVAL = "on_approval",
}

// Ratings & Asks (Client Experience) enums
export enum RatingTargetRole {
  AGENT = "agent",
  LENDER = "lender",
  ATTORNEY = "attorney",
}

export enum AskCategory {
  GENERAL = "general",
  FINANCIAL = "financial",
  TIMELINE = "timeline",
  INSPECTION = "inspection",
  CLOSING = "closing",
  OTHER = "other",
}

export enum AskScope {
  AGENT_ONLY = "agent_only",
  BOTH_AGENTS = "both_agents",
  DEAL_WIDE = "deal_wide",
}

export enum AskStatus {
  OPEN = "open",
  ANSWERED = "answered",
  CLOSED = "closed",
}

export enum AskReplyAuthorKind {
  INTERNAL = "internal",
  CLIENT = "client",
}

export enum DemoFormRole {
  AGENT = "agent",
  COORDINATOR = "coordinator",
  CLIENT = "client",
}

export enum ContactMethod {
  EMAIL = "email",
  PHONE = "phone",
  TEXT = "text",
}

export enum DemoPlan {
  STARTER = "starter",
  STUDIO = "studio",
  BROKERAGE = "brokerage",
}
